import type { DigestTimesResponseDto } from '@nomiss/sdk';
import {
  ApiRequestError,
  executeSdkRawRequest,
} from './api-request.util';
import { usersApi } from './sdk-client';

export type DigestTimeSlot = {
  id: string;
  time: string;
  displayTime: string;
};

export type DigestTimeGroup = {
  label: string;
  minTier: string;
  unlocked: boolean;
  slots: DigestTimeSlot[];
};

export type DigestTimeSelection = {
  primaryId: string | null;
  eveningId: string | null;
};

export type DigestTimesResponse = {
  tier: string;
  selected: DigestTimeSelection;
  groups: DigestTimeGroup[];
};

export type UpdateDigestTimesInput = {
  primaryId: string;
  eveningId?: string | null;
};

export class DigestTimeError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'DigestTimeError');
  }
}

function readSelectionId(
  record: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
): string | null {
  const value = record[camelKey] ?? record[snakeKey];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function normalizeSelection(selected: object): DigestTimeSelection {
  const record = selected as Record<string, unknown>;
  return {
    primaryId: readSelectionId(record, 'primaryId', 'primary_id'),
    eveningId: readSelectionId(record, 'eveningId', 'evening_id'),
  };
}

export function isEveningDigestTime(time: string): boolean {
  const hour = Number(time.slice(0, 2));
  return !Number.isNaN(hour) && hour >= 12;
}

function normalizeDigestTimesResponse(
  payload: DigestTimesResponseDto,
): DigestTimesResponse {
  return {
    tier: payload.tier,
    selected: normalizeSelection(payload.selected),
    groups: (payload.groups ?? []).map(group => ({
      label: group.label,
      minTier: group.minTier,
      unlocked: group.unlocked,
      slots: (group.slots ?? []).map(slot => ({
        id: slot.id,
        time: slot.time,
        displayTime: slot.displayTime,
      })),
    })),
  };
}

export async function getDigestTimes(): Promise<DigestTimesResponse> {
  const data = await executeSdkRawRequest<DigestTimesResponseDto>({
    request: () => usersApi.digestTimeControllerListV1Raw({}),
    fallback: 'Failed to load digest times',
    ErrorClass: DigestTimeError,
  });

  return normalizeDigestTimesResponse(data);
}

export async function updateDigestTimes(
  input: UpdateDigestTimesInput,
): Promise<DigestTimesResponse> {
  const updateDigestTimesDto = {
    primaryId: input.primaryId,
    ...(input.eveningId !== undefined ? { eveningId: input.eveningId } : {}),
  };

  const data = await executeSdkRawRequest<DigestTimesResponseDto>({
    request: () =>
      usersApi.digestTimeControllerUpdateV1Raw({
        updateDigestTimesDto,
      }),
    fallback: 'Failed to update digest time',
    ErrorClass: DigestTimeError,
  });

  return normalizeDigestTimesResponse(data);
}

export function findSelectedDigestTimeLabel(
  response: DigestTimesResponse,
): string | null {
  const slotById = new Map<string, string>();
  for (const group of response.groups) {
    for (const slot of group.slots) {
      slotById.set(slot.id, slot.displayTime);
    }
  }

  const primaryLabel = response.selected.primaryId
    ? (slotById.get(response.selected.primaryId) ?? null)
    : null;
  const eveningLabel = response.selected.eveningId
    ? (slotById.get(response.selected.eveningId) ?? null)
    : null;

  // Mirror onboarding summary: "8:00 AM · 7:00 PM" when both slots are set.
  if (primaryLabel && eveningLabel) {
    return `${primaryLabel} · ${eveningLabel}`;
  }

  return primaryLabel ?? eveningLabel ?? null;
}

const TIER_RANK: Record<string, number> = {
  free: 0,
  pro: 1,
  champion: 2,
};

/**
 * Reconcile group `unlocked` flags with the client's effective entitlement
 * (paid Champion or active free trial). Billing tier stays `free` during
 * trial, so a stale digest-times cache can still show Upgrade until refetch.
 */
export function applyEffectiveTierUnlockToDigestGroups(
  groups: DigestTimeGroup[],
  access: {
    tier: string;
    hasChampionPrivileges: boolean;
  },
): DigestTimeGroup[] {
  const effectiveTier = access.hasChampionPrivileges ? 'champion' : access.tier;
  const effectiveRank = TIER_RANK[effectiveTier] ?? 0;

  return groups.map(group => {
    const unlocked = (TIER_RANK[group.minTier] ?? 0) <= effectiveRank;
    if (group.unlocked === unlocked) {
      return group;
    }
    return { ...group, unlocked };
  });
}

export function findDigestTimeGroupForSlot(
  groups: DigestTimeGroup[],
  slotId: string,
): DigestTimeGroup | undefined {
  return groups.find(group => group.slots.some(slot => slot.id === slotId));
}

export function findDigestTimeSlot(
  groups: DigestTimeGroup[],
  slotId: string,
): DigestTimeSlot | undefined {
  for (const group of groups) {
    const slot = group.slots.find(candidate => candidate.id === slotId);
    if (slot) {
      return slot;
    }
  }
  return undefined;
}

export function findDefaultPrimaryDigestSlotId(
  groups: DigestTimeGroup[],
): string | null {
  const freeGroup = groups.find(group => group.minTier === 'free');
  return freeGroup?.slots[0]?.id ?? groups[0]?.slots[0]?.id ?? null;
}
